import { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import {
  HiOutlineArrowUpTray,
  HiOutlineExclamationTriangle,
  HiOutlineEye,
  HiOutlinePencilSquare,
  HiOutlineTrash,
} from "react-icons/hi2";
import SignatureDialog from "./signatureDialog";
import ConfirmationDialog from "../../../helpers/ConfirmationDialog";

const BLUE = "#0C86B6";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// The eight document slots, in the order the back office fills them.
export const POS_DOCUMENTS = [
  { name: "front_id", label: "وجه الهوية" },
  { name: "back_id", label: "ظهر الهوية" },
  { name: "living_front", label: "وجه بطاقة السكن" },
  { name: "living_back", label: "ظهر بطاقة السكن" },
  { name: "signature", label: "التوقيع", signature: true },
  { name: "finger_print", label: "بصمة الاصبع" },
  { name: "other_first_image", label: "مستمسك اخر" },
  { name: "other_second_image", label: "مستمسك اخر ٢" },
];

// A saved document arrives either as a plain URL or as an attachment object,
// so read whichever shape the API sends.
const savedUrlOf = (value) => {
  if (!value || value instanceof File) return null;
  if (typeof value === "string") return value.trim() || null;
  return value.path ?? value.url ?? value.image ?? null;
};

const ImageSlot = ({
  name,
  label,
  value,
  savedValue,
  signature,
  deleting,
  onChange,
  onDelete,
  onPreview,
}) => {
  const inputId = `pos-doc-${name}`;
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const isNew = value instanceof File;
  // Dropping a freshly picked file falls back to whatever is already stored on
  // the server, so cancelling never looks like a deletion.
  const savedUrl = savedUrlOf(value) ?? savedUrlOf(savedValue);

  // Freshly picked files are previewed from an object URL that has to be
  // released again, otherwise the blob stays alive for the whole session.
  const localUrl = useMemo(
    () => (isNew ? URL.createObjectURL(value) : null),
    [isNew, value],
  );
  useEffect(() => {
    return () => {
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [localUrl]);

  const previewUrl = localUrl ?? savedUrl;

  // A stored document whose URL is unreachable (a wrong storage host, a file
  // removed on the server) should say so rather than render a broken image.
  useEffect(() => {
    setLoadFailed(false);
  }, [previewUrl]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onChange(file);
    // Reset so re-picking the same file still fires a change event.
    e.target.value = "";
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.75, minHeight: 24 }}
      >
        <Typography sx={{ fontSize: 13.5, color: "#344054", fontWeight: 600 }}>
          {label}
        </Typography>
        {isNew && (
          <Chip
            label="جديد"
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: "rgba(12,134,182,0.12)",
              color: BLUE,
              fontWeight: 700,
            }}
          />
        )}
      </Stack>

      <Box
        sx={{
          position: "relative",
          height: 150,
          borderRadius: 2,
          border: previewUrl ? "1px solid #edf0f3" : "1px dashed #cbd5e1",
          bgcolor: previewUrl ? "#f8fafc" : "#fbfcfd",
          overflow: "hidden",
          transition: "border-color .15s ease",
          "&:hover": { borderColor: "rgba(12,134,182,0.6)" },
          "&:hover .slot-actions": { opacity: 1 },
        }}
      >
        {previewUrl ? (
          <>
            <Box
              component="img"
              src={previewUrl}
              alt={label}
              onError={() => setLoadFailed(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: loadFailed ? "none" : "block",
              }}
            />
            {loadFailed && (
              <Stack
                spacing={0.75}
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%", color: "#98a2b3", px: 1 }}
              >
                <Box sx={{ fontSize: 24, color: "#f79009", display: "flex" }}>
                  <HiOutlineExclamationTriangle />
                </Box>
                <Typography sx={{ fontSize: 12, textAlign: "center" }}>
                  تعذر تحميل الصورة
                </Typography>
              </Stack>
            )}
          </>
        ) : (
          <Stack
            component="label"
            htmlFor={signature ? undefined : inputId}
            onClick={signature ? () => setSignatureOpen(true) : undefined}
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", cursor: "pointer", color: "#98a2b3" }}
          >
            <Box sx={{ fontSize: 26, color: BLUE, display: "flex" }}>
              {signature ? <HiOutlinePencilSquare /> : <HiOutlineArrowUpTray />}
            </Box>
            <Typography sx={{ fontSize: 12.5 }}>
              {signature ? "اضغط للتوقيع" : "اضغط لاختيار صورة"}
            </Typography>
          </Stack>
        )}

        {previewUrl && (
          <Stack
            className="slot-actions"
            direction="row"
            spacing={0.5}
            sx={{
              position: "absolute",
              top: 6,
              left: 6,
              opacity: { xs: 1, md: 0 },
              transition: "opacity .15s ease",
            }}
          >
            <Tooltip title="عرض">
              <IconButton
                size="small"
                onClick={() => onPreview(previewUrl, label)}
                sx={{
                  bgcolor: "rgba(255,255,255,0.92)",
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                <HiOutlineEye size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={signature ? "اعادة التوقيع" : "تغيير الصورة"}>
              <IconButton
                size="small"
                component={signature ? "button" : "label"}
                htmlFor={signature ? undefined : inputId}
                onClick={signature ? () => setSignatureOpen(true) : undefined}
                sx={{
                  bgcolor: "rgba(255,255,255,0.92)",
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                <HiOutlinePencilSquare size={16} />
              </IconButton>
            </Tooltip>
            {/* A pick that has not been uploaded yet is only dropped locally;
                an already stored document goes through the delete endpoint. */}
            {isNew ? (
              <Tooltip title="الغاء الاختيار">
                <IconButton
                  size="small"
                  onClick={() => onChange(null)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.92)",
                    color: "#b42318",
                    "&:hover": { bgcolor: "#fff" },
                  }}
                >
                  <HiOutlineTrash size={16} />
                </IconButton>
              </Tooltip>
            ) : (
              !!onDelete && (
                <Tooltip title="حذف المستمسك">
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDelete(true)}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.92)",
                      color: "#b42318",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <HiOutlineTrash size={16} />
                  </IconButton>
                </Tooltip>
              )
            )}
          </Stack>
        )}

        {deleting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.7)",
            }}
          >
            <CircularProgress size={26} sx={{ color: BLUE }} />
          </Box>
        )}
      </Box>

      {signature && (
        <Button
          size="small"
          component="label"
          htmlFor={inputId}
          startIcon={<HiOutlineArrowUpTray size={14} />}
          sx={{ mt: 0.5, color: "#475467", fontSize: 12 }}
        >
          او ارفع صورة التوقيع
        </Button>
      )}

      <input
        id={inputId}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFile}
      />

      {signature && (
        <SignatureDialog
          open={signatureOpen}
          onClose={() => setSignatureOpen(false)}
          onSave={(file) => {
            onChange(file);
            setSignatureOpen(false);
          }}
        />
      )}

      <ConfirmationDialog
        open={confirmDelete}
        title="تأكيد الحذف"
        message="هل انت متأكد من حذف"
        name={label}
        confirmText="حذف"
        cancelText="إلغاء"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete();
        }}
      />
    </Box>
  );
};

// The documents grid: reads the current values from the form and hands back a
// File per slot. Uploading happens on submit, against `pos-image/{pos_id}`,
// while deleting a stored document hits the endpoint straight away.
const DocumentsSection = ({
  values,
  savedValues = {},
  deletingName,
  onChange,
  onDelete,
}) => {
  const [preview, setPreview] = useState(null);

  return (
    <>
      <Grid container spacing={2}>
        {POS_DOCUMENTS.map((doc) => (
          <Grid item xs={12} sm={6} md={3} key={doc.name}>
            <ImageSlot
              name={doc.name}
              label={doc.label}
              signature={doc.signature}
              value={values[doc.name]}
              savedValue={savedValues[doc.name]}
              deleting={deletingName === doc.name}
              onChange={(file) => onChange(doc.name, file)}
              onDelete={onDelete ? () => onDelete(doc.name) : undefined}
              onPreview={(url, label) => setPreview({ url, label })}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={!!preview}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        dir="rtl"
        onClose={() => setPreview(null)}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #edf0f3",
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#1f2a37" }}>
            {preview?.label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              onClick={() => window.open(preview?.url, "_blank")}
              sx={{ color: BLUE }}
            >
              فتح في تبويب جديد
            </Button>
            <IconButton onClick={() => setPreview(null)} size="small">
              <IoClose />
            </IconButton>
          </Stack>
        </Box>
        <Box
          sx={{
            bgcolor: "#f4f6f8",
            p: 2,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Box
            component="img"
            src={preview?.url}
            alt={preview?.label}
            sx={{ maxWidth: "100%", maxHeight: "72vh", objectFit: "contain" }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default DocumentsSection;
